function esc(str = '') {
  return String(str).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function pretty(body) {
  try {
    return JSON.stringify(typeof body === 'string' ? JSON.parse(body) : body, null, 2);
  } catch {
    return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
  }
}

function compact(body) {
  try {
    return JSON.stringify(typeof body === 'string' ? JSON.parse(body) : body);
  } catch {
    return typeof body === 'string' ? body : JSON.stringify(body);
  }
}

function requestContext(api, ctx = {}) {
  const base = (ctx.baseUrl || 'https://api.wiyak.com').replace(/\/$/, '');
  const url = base + api.endpoint;
  let body;
  try {
    body = JSON.parse(api.requestExample || '{}');
  } catch {
    body = {};
  }
  if (api.id !== 'login' && ctx.token) body.token = ctx.token;

  const headers = { 'Content-Type': 'application/json' };
  const keys = (api.headers || []).map(h => h.key);
  if (keys.includes('bearerkey')) {
    headers.bearerkey = ctx.bearerKey || 'XXXXXXXXXXXXXXXXXXXX';
  }
  if (keys.includes('Authorization')) {
    headers.Authorization = `Bearer ${ctx.token || 'YOUR_JWT_TOKEN'}`;
  }
  return { url, headers, body, json: pretty(body), jsonOne: compact(body) };
}

function headerLines(headers, fmt) {
  return Object.entries(headers).map(([k, v]) => fmt(k, v)).join('\n');
}

export const SNIPPET_CONFIG = {
  shell: {
    label: 'cURL',
    variants: { curl: 'cURL', httpie: 'HTTPie', wget: 'wget' },
  },
  javascript: {
    label: 'JavaScript',
    variants: { fetch: 'Fetch', axios: 'Axios', xhr: 'XHR' },
  },
  java: {
    label: 'Java',
    variants: { okhttp: 'OkHttp', unirest: 'Unirest' },
  },
  python: {
    label: 'Python',
    variants: { requests: 'Requests', httpx: 'HTTPX' },
  },
  node: {
    label: 'Node.js',
    variants: { fetch: 'fetch', axios: 'Axios' },
  },
  php: {
    label: 'PHP',
    variants: { curl: 'cURL', guzzle: 'Guzzle' },
  },
  csharp: {
    label: 'C#',
    variants: { httpclient: 'HttpClient' },
  },
  go: {
    label: 'Go',
    variants: { nethttp: 'net/http' },
  },
  ruby: {
    label: 'Ruby',
    variants: { nethttp: 'Net::HTTP' },
  },
  dart: {
    label: 'Dart',
    variants: { http: 'http' },
  },
  swift: {
    label: 'Swift',
    variants: { urlsession: 'URLSession' },
  },
  kotlin: {
    label: 'Kotlin',
    variants: { okhttp: 'OkHttp' },
  },
  rust: {
    label: 'Rust',
    variants: { reqwest: 'reqwest' },
  },
  powershell: {
    label: 'PowerShell',
    variants: { irm: 'Invoke-RestMethod' },
  },
  r: {
    label: 'R',
    variants: { httr: 'httr' },
  },
};

function toCurl({ url, headers, json }) {
  const h = headerLines(headers, (k, v) => `  -H "${k}: ${esc(v)}"`);
  return `curl -X POST "${url}" \\\n${h} \\\n  -d '${json}'`;
}

function toHttpie({ url, headers, jsonOne }) {
  const h = Object.entries(headers).map(([k, v]) => `${k}:"${esc(v)}"`).join(' ');
  return `http POST ${url} ${h} <<< '${jsonOne}'`;
}

function toWget({ url, headers, json }) {
  const h = headerLines(headers, (k, v) => `  --header="${k}: ${esc(v)}"`);
  return `wget --method=POST \\\n${h} \\\n  --body-data='${json}' \\\n  "${url}"`;
}

function toFetch({ url, headers, json }) {
  return `fetch("${url}", {\n  method: "POST",\n  headers: ${JSON.stringify(headers, null, 2)},\n  body: JSON.stringify(${json})\n})\n  .then(res => res.json())\n  .then(console.log)\n  .catch(console.error);`;
}

function toAxios({ url, headers, json }) {
  return `import axios from "axios";\n\nconst { data } = await axios.post(\n  "${url}",\n  ${json},\n  { headers: ${JSON.stringify(headers, null, 2)} }\n);\nconsole.log(data);`;
}

function toXhr({ url, headers, jsonOne }) {
  const set = Object.entries(headers).map(([k, v]) => `xhr.setRequestHeader("${k}", "${esc(v)}");`).join('\n');
  return `const xhr = new XMLHttpRequest();\nxhr.open("POST", "${url}");\n${set}\nxhr.onload = () => console.log(xhr.responseText);\nxhr.send('${jsonOne}');`;
}

function toOkHttp({ url, headers, jsonOne }) {
  const h = Object.entries(headers).map(([k, v]) => `      .addHeader("${k}", "${esc(v)}")`).join('\n');
  return `OkHttpClient client = new OkHttpClient();\nRequestBody body = RequestBody.create(\n    "${esc(jsonOne)}", MediaType.parse("application/json"));\nRequest request = new Request.Builder()\n      .url("${url}")\n${h}\n      .post(body)\n      .build();\ntry (Response response = client.newCall(request).execute()) {\n  System.out.println(response.body().string());\n}`;
}

function toUnirest({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `  .header("${k}", "${esc(v)}")`).join('\n');
  return `HttpResponse<String> response = Unirest.post("${url}")\n${h}\n  .body(${JSON.stringify(json)})\n  .asString();\nSystem.out.println(response.getBody());`;
}

function toRequests({ url, headers, json }) {
  return `import requests\n\nurl = "${url}"\nheaders = ${JSON.stringify(headers, null, 2)}\npayload = ${json}\n\nres = requests.post(url, json=payload, headers=headers)\nprint(res.json())`;
}

function toHttpx({ url, headers, json }) {
  return `import httpx\n\nurl = "${url}"\nheaders = ${JSON.stringify(headers, null, 2)}\npayload = ${json}\n\nres = httpx.post(url, json=payload, headers=headers)\nprint(res.json())`;
}

function toPhpCurl({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `  "${k}: ${esc(v)}",`).join('\n');
  return `<?php\n$ch = curl_init("${url}");\ncurl_setopt_array($ch, [\n  CURLOPT_RETURNTRANSFER => true,\n  CURLOPT_POST => true,\n  CURLOPT_HTTPHEADER => [\n${h}\n  ],\n  CURLOPT_POSTFIELDS => '${json}',\n]);\n$response = curl_exec($ch);\ncurl_close($ch);\necho $response;`;
}

function toGuzzle({ url, headers, json }) {
  return `<?php\n$client = new \\GuzzleHttp\\Client();\n$response = $client->post("${url}", [\n  "headers" => ${JSON.stringify(headers, null, 2)},\n  "json" => json_decode('${compact(json)}', true),\n]);\necho $response->getBody();`;
}

function toCsharp({ url, headers, json }) {
  const h = Object.entries(headers)
    .filter(([k]) => k.toLowerCase() !== 'content-type')
    .map(([k, v]) => `client.DefaultRequestHeaders.Add("${k}", "${esc(v)}");`)
    .join('\n');
  return `using var client = new HttpClient();\n${h}\nvar content = new StringContent("""\n${json}\n""", Encoding.UTF8, "application/json");\nvar res = await client.PostAsync("${url}", content);\nConsole.WriteLine(await res.Content.ReadAsStringAsync());`;
}

function toGo({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `\treq.Header.Set("${k}", "${esc(v)}")`).join('\n');
  return `package main\n\nimport (\n\t"bytes"\n\t"fmt"\n\t"io"\n\t"net/http"\n)\n\nfunc main() {\n\tbody := []byte(\`${json}\`)\n\treq, _ := http.NewRequest("POST", "${url}", bytes.NewBuffer(body))\n${h}\n\tres, err := http.DefaultClient.Do(req)\n\tif err != nil { panic(err) }\n\tdefer res.Body.Close()\n\tb, _ := io.ReadAll(res.Body)\n\tfmt.Println(string(b))\n}`;
}

function toRuby({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `req["${k}"] = "${esc(v)}"`).join('\n');
  return `require "net/http"\nrequire "uri"\nrequire "json"\n\nuri = URI("${url}")\nreq = Net::HTTP::Post.new(uri)\n${h}\nreq.body = <<~JSON\n${json}\nJSON\n\nres = Net::HTTP.start(uri.hostname, uri.port, use_ssl: uri.scheme == "https") do |http|\n  http.request(req)\nend\nputs res.body`;
}

function toDart({ url, headers, json }) {
  return `import 'dart:convert';\nimport 'package:http/http.dart' as http;\n\nfinal res = await http.post(\n  Uri.parse("${url}"),\n  headers: ${JSON.stringify(headers, null, 2)},\n  body: jsonEncode(${json}),\n);\nprint(res.body);`;
}

function toSwift({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `request.setValue("${esc(v)}", forHTTPHeaderField: "${k}")`).join('\n');
  return `import Foundation\n\nlet url = URL(string: "${url}")!\nvar request = URLRequest(url: url)\nrequest.httpMethod = "POST"\n${h}\nrequest.httpBody = """\n${json}\n""".data(using: .utf8)\n\nURLSession.shared.dataTask(with: request) { data, _, error in\n  if let data { print(String(data: data, encoding: .utf8)! ) }\n}.resume()`;
}

function toKotlin({ url, headers, jsonOne }) {
  const h = Object.entries(headers).map(([k, v]) => `    .addHeader("${k}", "${esc(v)}")`).join('\n');
  return `val client = OkHttpClient()\nval body = "${esc(jsonOne)}".toRequestBody("application/json".toMediaType())\nval request = Request.Builder()\n    .url("${url}")\n${h}\n    .post(body)\n    .build()\nclient.newCall(request).execute().use { println(it.body?.string()) }`;
}

function toRust({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `        .header("${k}", "${esc(v)}")`).join('\n');
  return `use reqwest::Client;\n\nlet client = Client::new();\nlet res = client.post("${url}")\n${h}\n        .body(r#"${json}"#)\n        .send()\n        .await?;\nprintln!("{}", res.text().await?);`;
}

function toPowerShell({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `  "${k}" = "${esc(v)}"`).join('\n');
  return `$headers = @{\n${h}\n}\n$body = @'\n${json}\n'@\nInvoke-RestMethod -Method Post -Uri "${url}" -Headers $headers -Body $body -ContentType "application/json"`;
}

function toR({ url, headers, json }) {
  const h = Object.entries(headers).map(([k, v]) => `  \`${k}\` = "${esc(v)}"`).join(',\n');
  return `library(httr)\n\nres <- POST(\n  "${url}",\n  add_headers(\n${h}\n  ),\n  body = '${json}',\n  encode = "raw"\n)\ncontent(res, as = "text")`;
}

const GENERATORS = {
  shell: { curl: toCurl, httpie: toHttpie, wget: toWget },
  javascript: { fetch: toFetch, axios: toAxios, xhr: toXhr },
  java: { okhttp: toOkHttp, unirest: toUnirest },
  python: { requests: toRequests, httpx: toHttpx },
  node: { fetch: toFetch, axios: toAxios },
  php: { curl: toPhpCurl, guzzle: toGuzzle },
  csharp: { httpclient: toCsharp },
  go: { nethttp: toGo },
  ruby: { nethttp: toRuby },
  dart: { http: toDart },
  swift: { urlsession: toSwift },
  kotlin: { okhttp: toKotlin },
  rust: { reqwest: toRust },
  powershell: { irm: toPowerShell },
  r: { httr: toR },
};

export function generateSnippet(api, lang, variant, ctx = {}) {
  const req = requestContext(api, ctx);
  const langGens = GENERATORS[lang] || GENERATORS.shell;
  const fn = langGens[variant] || Object.values(langGens)[0];
  return fn(req);
}
